import React from "react";
import { Link } from "react-router-dom";
import { Bird, ArrowLeft, ShieldCheck } from "lucide-react";

const sections = [
  {
    title: "1. Who we are and how to reach us",
    body: (
      <>
        <p>
          Never Alone in Singapore (&quot;Never Alone&quot;, &quot;we&quot;,
          &quot;us&quot;, &quot;our&quot;) is a private peer community based in
          Singapore. This Privacy Statement explains how we collect, use,
          disclose, and protect personal data in line with the Singapore{" "}
          <span className="font-semibold">
            Personal Data Protection Act 2012 (PDPA)
          </span>{" "}
          and its subsequent amendments.
        </p>
        <p className="mt-3">
          Our Data Protection Officer (DPO) can be contacted at{" "}
          <a
            href="mailto:drkev@drkevinkwok.org"
            className="font-semibold text-[#3d6486] hover:text-[#2c3e50] underline-offset-4 hover:underline"
          >
            drkev@drkevinkwok.org
          </a>{" "}
          or by post at 16 Raffles Quay #14-05, Singapore 048581, or by phone
          at +65 8366 9817. Any privacy question, access request, withdrawal of
          consent, or complaint can be sent there, and a real person will
          respond within 30 calendar days.
        </p>
      </>
    ),
  },
  {
    title: "2. The personal data we collect",
    body: (
      <>
        <p>We try to collect only what we genuinely need:</p>
        <ul className="mt-3 space-y-2 list-disc pl-5">
          <li>
            <span className="font-semibold">Account data</span> you give us when
            signing up: your first name and email address.
          </li>
          <li>
            <span className="font-semibold">Community content</span> you choose
            to share inside the community (posts, replies, reactions).
          </li>
          <li>
            <span className="font-semibold">Technical data</span> automatically
            generated when you visit the site: IP address, browser type, device
            type, pages visited, and timestamps. This is used only for security,
            abuse prevention, and basic site analytics.
          </li>
          <li>
            <span className="font-semibold">Correspondence</span> you send us
            (for example, emails to our DPO or moderators).
          </li>
        </ul>
        <p className="mt-3">
          We do <span className="font-semibold">not</span> knowingly collect
          personal data from anyone under 13. If you are between 13 and 18, you
          should review this statement with a parent or guardian.
        </p>
      </>
    ),
  },
  {
    title: "3. Why we use your personal data (Purpose Limitation)",
    body: (
      <>
        <p>We use personal data only for purposes a reasonable person would
        expect, namely:</p>
        <ul className="mt-3 space-y-2 list-disc pl-5">
          <li>Creating your community membership and welcoming you.</li>
          <li>Sending occasional community emails (e.g. updates, gentle
          check-ins, important changes to our terms).</li>
          <li>Moderating the community to keep it safe and kind.</li>
          <li>Preventing abuse, fraud, spam, scraping, and unauthorised access.</li>
          <li>Complying with Singapore law, including responses to lawful
          requests by authorities.</li>
        </ul>
        <p className="mt-3">
          We will not use your personal data for any new purpose without first
          notifying you and, where required, obtaining your consent.
        </p>
      </>
    ),
  },
  {
    title: "4. Consent and how you can withdraw it",
    body: (
      <>
        <p>
          By submitting the sign-up form, you give us consent under the PDPA to
          collect, use, and disclose your personal data for the purposes set out
          in Section 3.
        </p>
        <p className="mt-3">
          You can withdraw consent at any time by writing to{" "}
          <a
            href="mailto:drkev@drkevinkwok.org"
            className="font-semibold text-[#3d6486] hover:text-[#2c3e50] underline-offset-4 hover:underline"
          >
            drkev@drkevinkwok.org
          </a>
          . Once we receive your request, we will action it within a reasonable
          period (usually within 10 business days) and inform you of any
          consequences (for example, closing your community account if it can no
          longer function without that data).
        </p>
      </>
    ),
  },
  {
    title: "5. Your rights: access and correction",
    body: (
      <>
        <p>
          You have the right under the PDPA to:
        </p>
        <ul className="mt-3 space-y-2 list-disc pl-5">
          <li>
            <span className="font-semibold">Access</span> the personal data we
            hold about you and information about how it has been used or
            disclosed in the past year.
          </li>
          <li>
            <span className="font-semibold">Correct</span> any personal data
            that is inaccurate or out of date.
          </li>
        </ul>
        <p className="mt-3">
          Send your request to{" "}
          <a
            href="mailto:drkev@drkevinkwok.org"
            className="font-semibold text-[#3d6486] hover:text-[#2c3e50] underline-offset-4 hover:underline"
          >
            drkev@drkevinkwok.org
          </a>
          . We may ask for proof of identity to make sure data is not given to
          the wrong person. A reasonable fee may apply for very large access
          requests; we will tell you before charging anything.
        </p>
      </>
    ),
  },
  {
    title: "6. Accuracy and protection of your data",
    body: (
      <>
        <p>
          We make reasonable efforts to keep personal data accurate, complete,
          and up to date when it is used to make a decision affecting you, or
          when it is disclosed to another organisation.
        </p>
        <p className="mt-3">
          We protect personal data with reasonable technical and organisational
          measures, including:
        </p>
        <ul className="mt-3 space-y-2 list-disc pl-5">
          <li>Encryption in transit (HTTPS/TLS).</li>
          <li>Access controls so only authorised moderators can see member data.</li>
          <li>Logging and monitoring for suspicious activity.</li>
          <li>Regular review of who has access and why.</li>
        </ul>
        <p className="mt-3">
          No system is perfectly secure, but we treat your trust as the most
          important thing we hold.
        </p>
      </>
    ),
  },
  {
    title: "7. How long we keep your data (Retention Limitation)",
    body: (
      <>
        <p>
          We stop retaining personal data as soon as it is no longer needed for
          the purposes in Section 3, or for any legal or business reason.
        </p>
        <ul className="mt-3 space-y-2 list-disc pl-5">
          <li>
            Active member data is kept while your account is open.
          </li>
          <li>
            If you close your account or withdraw consent, we will delete or
            anonymise your personal data within 90 days, except where we are
            required to keep it longer (for example, to deal with a legal claim
            or safety report).
          </li>
          <li>
            Technical logs are typically kept for no more than 12 months.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "8. Who we share data with (Disclosure)",
    body: (
      <>
        <p>
          We do <span className="font-semibold">not</span> sell, rent, or trade
          your personal data. Ever.
        </p>
        <p className="mt-3">
          We share personal data only with:
        </p>
        <ul className="mt-3 space-y-2 list-disc pl-5">
          <li>
            <span className="font-semibold">Trusted service providers</span>{" "}
            (for example, our database host and email provider) who act on our
            instructions and are bound by confidentiality and PDPA-equivalent
            protections.
          </li>
          <li>
            <span className="font-semibold">Authorities</span> when required by
            Singapore law, court order, or to protect the safety of any person.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "9. Overseas transfer (Transfer Limitation)",
    body: (
      <>
        <p>
          Some of our service providers may store data outside Singapore (for
          example, in regional data centres). Before transferring personal data
          overseas, we make sure the receiving organisation is bound by legally
          enforceable obligations that provide a standard of protection at least
          comparable to the PDPA, as required by the PDPA Transfer Limitation
          Obligation.
        </p>
      </>
    ),
  },
  {
    title: "10. Data breach notification",
    body: (
      <>
        <p>
          In the unfortunate event of a notifiable data breach (one that is
          likely to result in significant harm to affected individuals, or that
          affects 500 or more individuals), we will:
        </p>
        <ul className="mt-3 space-y-2 list-disc pl-5">
          <li>
            Notify the Personal Data Protection Commission (PDPC) as soon as
            practicable, and in any case within 3 calendar days.
          </li>
          <li>
            Notify affected individuals without undue delay, with clear
            information on what happened and what we are doing about it.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "11. Marketing messages and the Do Not Call (DNC) Registry",
    body: (
      <>
        <p>
          We do not send marketing SMS or make telemarketing calls. We may send
          you community emails (welcomes, updates, important changes). Every
          email includes a clear way to unsubscribe, and unsubscribing takes
          effect immediately.
        </p>
        <p className="mt-3">
          If we ever start sending marketing messages to Singapore phone numbers
          in the future, we will check the DNC Registry first and obtain your
          clear and unambiguous consent in writing, as required by the PDPA.
        </p>
      </>
    ),
  },
  {
    title: "12. Cookies and similar technologies",
    body: (
      <>
        <p>
          We use only essential cookies and local storage needed to keep you
          signed in to the admin area and to remember basic preferences. We do
          not use third-party advertising trackers. If we ever add optional
          analytics, we will ask for your consent first.
        </p>
      </>
    ),
  },
  {
    title: "13. Complaints",
    body: (
      <>
        <p>
          If you believe we have not handled your personal data in line with the
          PDPA, please contact our DPO first at{" "}
          <a
            href="mailto:drkev@drkevinkwok.org"
            className="font-semibold text-[#3d6486] hover:text-[#2c3e50] underline-offset-4 hover:underline"
          >
            drkev@drkevinkwok.org
          </a>
          . We will investigate and respond within 30 calendar days.
        </p>
        <p className="mt-3">
          You also have the right to lodge a complaint with the Personal Data
          Protection Commission (PDPC) Singapore at{" "}
          <a
            href="https://www.pdpc.gov.sg"
            target="_blank"
            rel="noreferrer"
            className="font-semibold text-[#3d6486] hover:text-[#2c3e50] underline-offset-4 hover:underline"
          >
            pdpc.gov.sg
          </a>
          .
        </p>
      </>
    ),
  },
  {
    title: "14. Changes to this statement",
    body: (
      <>
        <p>
          We may update this Privacy Statement from time to time. If we make
          material changes, we will notify members by email and update the
          &quot;Last updated&quot; date below. Continued use of Never Alone in
          Singapore after a change means you accept the updated statement.
        </p>
      </>
    ),
  },
];

const Privacy = () => {
  return (
    <div className="min-h-screen bg-[#f6f9fc]">
      <header className="bg-[#354556] text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/10 border border-white/20">
              <Bird className="h-5 w-5 text-[#cfe7f8]" />
            </span>
            <span className="ht-display text-lg">Never Alone SG</span>
          </Link>
          <Link
            to="/"
            className="ht-btn inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 px-4 py-2 text-sm"
          >
            <ArrowLeft className="h-4 w-4" /> Back to site
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
        <div className="flex items-center gap-2 text-[#4a90c2]">
          <ShieldCheck className="h-4 w-4" />
          <p className="font-semibold uppercase tracking-[0.18em] text-xs ht-body">
            Privacy Statement
          </p>
        </div>
        <h1 className="mt-3 ht-display text-3xl sm:text-4xl text-[#2c3e50] leading-[1.15]">
          Your story is yours. We just hold the door.
        </h1>
        <p className="mt-5 ht-body text-[#5a6b7d] text-base sm:text-lg leading-relaxed">
          This statement explains how Never Alone in Singapore collects, uses,
          discloses, and protects your personal data, in line with the
          Singapore Personal Data Protection Act 2012 (PDPA). It is written in
          plain language so you can actually read it.
        </p>

        <div className="mt-10 space-y-6">
          {sections.map((s) => (
            <section
              key={s.title}
              className="ht-card rounded-2xl bg-white border border-[#e6eef5] p-6 sm:p-7 shadow-sm"
            >
              <h2 className="ht-display text-lg sm:text-xl text-[#2c3e50] leading-snug">
                {s.title}
              </h2>
              <div className="mt-3 ht-body text-[#5a6b7d] leading-relaxed">
                {s.body}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-10 rounded-2xl bg-[#eaf4fb] border border-[#cfe7f8] p-6 sm:p-7">
          <p className="ht-body text-[#2c3e50]">
            Any privacy concern, access request, or correction request:{" "}
            <a
              href="mailto:drkev@drkevinkwok.org"
              className="font-semibold text-[#3d6486] hover:text-[#2c3e50] underline-offset-4 hover:underline"
            >
              drkev@drkevinkwok.org
            </a>
            . A real person reads every message.
          </p>
        </div>

        <p className="mt-8 text-xs text-[#7e8a96]">
          Last updated: June 2026. This statement is intended to be PDPA-aligned
          and is provided in good faith. It is not legal advice. Singapore law
          applies.
        </p>
      </main>
    </div>
  );
};

export default Privacy;
